import { useCallback, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { NetworkGraph } from '@/components/charts/NetworkGraph'
import { StatTile } from '@/components/charts/StatTile'
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
} from '@/components/ui'
import { useQuery } from '@/hooks/useQuery'
import { ApiError, api } from '@/lib/api'
import type { GraphResponse, SemanticSearchResult } from '@/types'

export function NetworkPage() {
  const navigate = useNavigate()
  const [depth, setDepth] = useState(2)

  const graph = useQuery(
    useCallback(() => api.get<GraphResponse>('/graph', { depth, limit: 300 }), [depth]),
    [depth],
  )

  return (
    <>
      <PageHeader
        title="Network"
        subtitle="A projection of the derived layer — people, the organisations they belong to, and what they offer or need."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="People" value={graph.data?.stats.people ?? 0} />
        <StatTile label="Companies" value={graph.data?.stats.companies ?? 0} />
        <StatTile
          label="Skills & services"
          value={(graph.data?.stats.skills ?? 0) + (graph.data?.stats.services ?? 0)}
        />
        <StatTile
          label="Relationships"
          value={graph.data?.stats.edges ?? 0}
          hint={`${graph.data?.stats.nodes ?? 0} nodes in total`}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <Card
          className="xl:col-span-3"
          title="Relationship graph"
          subtitle="Click a person to open their profile"
          action={
            <div className="flex items-center gap-1">
              {[1, 2, 3].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={
                    depth === value
                      ? 'btn-secondary px-2 py-1 text-xs font-medium'
                      : 'btn-ghost px-2 py-1 text-xs'
                  }
                  aria-pressed={depth === value}
                  onClick={() => setDepth(value)}
                >
                  {value} hop{value > 1 ? 's' : ''}
                </button>
              ))}
            </div>
          }
        >
          {graph.loading && !graph.data ? (
            <Spinner label="Laying out the graph" />
          ) : graph.error ? (
            <ErrorNote message={graph.error} onRetry={graph.reload} />
          ) : (
            <NetworkGraph
              nodes={graph.data?.nodes ?? []}
              edges={graph.data?.edges ?? []}
              onSelectContact={(contactId) => navigate(`/contacts/${contactId}`)}
            />
          )}
        </Card>

        <SemanticSearch />
      </div>
    </>
  )
}

function SemanticSearch() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [target, setTarget] = useState<'offer' | 'need'>('offer')
  const [results, setResults] = useState<SemanticSearchResult[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function search(event: FormEvent) {
    event.preventDefault()
    if (query.trim().length < 2) return
    setBusy(true)
    setError(null)
    try {
      setResults(
        await api.get<SemanticSearchResult[]>('/search', {
          q: query.trim(),
          target,
          limit: 15,
        }),
      )
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Search failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Find someone" subtitle="Semantic search over the network, not keywords">
      <form onSubmit={search} className="space-y-2.5 px-4 py-4">
        <div className="flex gap-1 rounded-md p-0.5" style={{ background: 'var(--surface-2)' }}>
          {(
            [
              ['offer', 'Who can do this?'],
              ['need', 'Who wants this?'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTarget(value)}
              aria-pressed={target === value}
              className={
                target === value
                  ? 'flex-1 rounded bg-surface px-2 py-1 text-xs font-medium text-ink shadow-sm'
                  : 'flex-1 rounded px-2 py-1 text-xs text-ink-muted'
              }
            >
              {label}
            </button>
          ))}
        </div>

        <input
          type="search"
          className="field"
          placeholder={target === 'offer' ? 'shopify development' : 'e-commerce clients'}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Semantic search"
        />
        <button type="submit" className="btn-primary w-full" disabled={busy || query.trim().length < 2}>
          {busy ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <p className="px-4 pb-3 text-xs" style={{ color: 'var(--critical)' }} role="alert">
          {error}
        </p>
      )}

      {results !== null &&
        (results.length === 0 ? (
          <EmptyState
            title="Nothing close enough"
            hint="Semantic search needs an embedding provider. Groq does not offer one, so
                  set OPENAI_API_KEY (or EMBEDDING_BASE_URL for a self-hosted endpoint) to
                  turn it on — matching itself keeps working without it."
          />
        ) : (
          <ul className="divide-y divide-line border-t border-line">
            {results.map((result) => (
              <li key={result.contact_id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-raised"
                  onClick={() => navigate(`/contacts/${result.contact_id}`)}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-ink">{result.name}</span>
                    <span className="block truncate text-xs text-ink-muted">
                      {result.headline || '—'}
                    </span>
                  </span>
                  <Badge tone="info">{Math.round(result.similarity * 100)}%</Badge>
                </button>
              </li>
            ))}
          </ul>
        ))}
    </Card>
  )
}
